import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetUserId: string;
  targetEmail: string | null;
  targetLabel: string;
  onDeleted: () => void;
}

export const DeleteUserDialog = ({
  open,
  onOpenChange,
  targetUserId,
  targetEmail,
  targetLabel,
  onDeleted,
}: DeleteUserDialogProps) => {
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);

  const expected = (targetEmail ?? "").trim().toLowerCase();
  const matches = expected.length > 0 && confirmation.trim().toLowerCase() === expected;

  const reset = () => {
    setConfirmation("");
    setBusy(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (busy) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const handleDelete = async () => {
    if (!matches || busy) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("admin-delete-user", {
      body: {
        target_user_id: targetUserId,
        confirmation: targetEmail,
      },
    });
    setBusy(false);
    if (error) {
      toast.error(error.message ?? "Delete failed");
      return;
    }
    const result = data as {
      profiles_deleted?: number;
      subs_canceled?: number;
      stripe_errors?: Array<{ error: string }>;
    } | null;
    const profiles = result?.profiles_deleted ?? 0;
    const subs = result?.subs_canceled ?? 0;
    const stripeErrCount = result?.stripe_errors?.length ?? 0;
    let summary = `Deleted ${profiles} workspace${profiles === 1 ? "" : "s"}`;
    if (subs > 0) summary += `, canceled ${subs} subscription${subs === 1 ? "" : "s"}`;
    if (stripeErrCount > 0) summary += ` (${stripeErrCount} Stripe error${stripeErrCount === 1 ? "" : "s"} — see logs)`;
    toast.success(summary);
    reset();
    onOpenChange(false);
    onDeleted();
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Permanently delete this account?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <div>
                Target: <span className="font-medium text-foreground">{targetLabel}</span>
                {targetEmail && (
                  <span className="text-muted-foreground"> · {targetEmail}</span>
                )}
              </div>
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-destructive">
                This immediately cancels any active subscriptions and deletes
                every Persona, workspace, excerpt, library item, draft,
                and upload owned by this user. This cannot be undone.
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Label htmlFor="delete-user-confirm">
            Type the email <span className="font-mono">{targetEmail}</span> to confirm
          </Label>
          <Input
            id="delete-user-confirm"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder={targetEmail ?? ""}
            disabled={busy}
          />
        </div>

        <AlertDialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!matches || busy}
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Deleting…
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" /> Permanently delete
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
